import { useRef, useState } from "react";

import clsx from "clsx";
import moment from "moment";
import { useOnClickOutside } from "usehooks-ts";

import { Calendar, PrimaryButton, SecondaryButton } from "@components/v2";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { type FixedDateInterval } from "@north-beam/nb-common";

interface Props {
  label: string;
  dateRange: FixedDateInterval;
  minDate: string;
  onClose: () => void;
  onChange: (val: FixedDateInterval) => void;
}

export const DatePicker = ({
  label,
  minDate,
  dateRange,
  onClose,
  onChange,
}: Props) => {
  const containerRef = useRef(null);

  const [localDateRange, setLocalDateRange] = useState(dateRange);

  useOnClickOutside(containerRef, () => {
    onClose();
  });

  const { refs, floatingStyles } = useFloating({
    open: true,
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
    middleware: [offset(4), flip(), shift()],
  });

  return (
    <div ref={containerRef} className="inline-block">
      <div
        ref={refs.setReference}
        className="font-semibold inline-flex items-center gap-1 text-v2-action"
      >
        <span className="underline">{label}</span>
        <i className="fa-regular fa-angle-up text-base" />
      </div>
      <div
        className={clsx(
          "w-[580px] bg-v2-highlight px-6 py-4 flex flex-col justify-between",
          "z-[9999] border border-v2-secondary rounded-md elevation-2",
        )}
        ref={refs.setFloating}
        style={floatingStyles}
      >
        <div>
          <Calendar
            minDate={minDate}
            maxDate={moment().format("YYYY-MM-DD")}
            value={localDateRange}
            onChange={(val) => setLocalDateRange({ ...val, type: "fixed" })}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <SecondaryButton size="sm" onClick={() => onClose()}>
            CANCEL
          </SecondaryButton>
          <PrimaryButton
            size="sm"
            onClick={() => {
              onChange(localDateRange);
              onClose();
            }}
          >
            DONE
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
